/** Tab panel bodies for the HERO Dokumente detail left pane. */
import { ld } from "@/lib/theme/light-dark";
import { CalendarDays, Home, Link2, Plus, Tag } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H } from "../../hero-tokens";
import type { DocDetailFixture, DocDetailTab } from "./module-dokumente-data";

const FIELD_LABEL: CSSProperties = {
  fontFamily: H.label,
  fontSize: 9.5,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color: ld("rgba(11,15,25,0.32)", "rgba(250,250,250,0.32)"),
  marginBottom: 5,
};

const FIELD_INPUT: CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 10px",
  borderRadius: 8,
  border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
  background: ld("#FAFAFB", "#1e2124"),
  fontSize: 12.5,
  color: H.ink,
  fontFamily: H.sans,
};

function SectionLabel(props: { readonly label: string }): ReactNode {
  return (
    <div style={{ ...FIELD_LABEL, marginTop: 14, marginBottom: 8 }}>
      {props.label}
    </div>
  );
}

function TagPill(props: {
  readonly label: string;
  readonly color: string;
}): ReactNode {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 9px",
        borderRadius: 6,
        fontSize: 11.5,
        fontWeight: 500,
        // i18n-exempt: CSS color value
        background: `color-mix(in srgb, ${props.color} 9.4%, transparent)`,
        color: props.color,
      }}
    >
      <Tag width={11} height={11} strokeWidth={2} />
      {props.label}
    </span>
  );
}

function ContactChip(props: {
  readonly name: string;
  readonly initials: string;
}): ReactNode {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
      <span
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          background: ld("#ECEEF1", "#2e3135"),
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 9,
          fontWeight: 600,
          color: H.inkSoft,
        }}
      >
        {props.initials}
      </span>
      <span style={{ fontSize: 12.5, color: H.ink }}>{props.name}</span>
    </span>
  );
}

function DetailsTabBody(props: {
  readonly fixture: DocDetailFixture;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteDetailTabBodies");
  const f = props.fixture;
  return (
    <div style={{ padding: "12px 14px 16px" }}>
      <div style={FIELD_LABEL}>{t("titel")}</div>
      <div style={FIELD_INPUT}>{f.title}</div>
      <div style={{ ...FIELD_LABEL, marginTop: 12 }}>
        {t("ausstellungsdatum")}
      </div>
      <div
        style={{
          ...FIELD_INPUT,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <CalendarDays
          width={14}
          height={14}
          strokeWidth={1.8}
          color={H.inkDim}
        />
        {f.issuedAt ?? t("datumWaehlen")}
      </div>
      {f.tags.length > 0 ? (
        <>
          <SectionLabel label={t("tags")} />
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              alignItems: "center",
            }}
          >
            {f.tags.map((tag) => (
              <TagPill key={tag.label} label={tag.label} color={tag.color} />
            ))}
            <Plus width={14} height={14} strokeWidth={1.8} color={H.inkGhost} />
          </div>
        </>
      ) : null}
      {f.contacts.length > 0 ? (
        <>
          <SectionLabel label={t("kontakte")} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {f.contacts.map((c) => (
              <ContactChip key={c.name} name={c.name} initials={c.initials} />
            ))}
          </div>
        </>
      ) : null}
      {f.properties.length > 0 ? (
        <>
          <SectionLabel label={t("liegenschaften")} />
          {f.properties.map((p) => (
            <span
              key={p.name}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                fontSize: 12.5,
              }}
            >
              <Home width={14} height={14} strokeWidth={1.8} color={H.inkDim} />
              {p.name}
            </span>
          ))}
        </>
      ) : null}
      {f.units.length > 0 ? (
        <>
          <SectionLabel label={t("einheiten")} />
          {f.units.map((u) => (
            <span key={u.name} style={{ fontSize: 12.5, color: H.ink }}>
              {u.name}
            </span>
          ))}
        </>
      ) : null}
      {f.vorgang !== null ? (
        <>
          <SectionLabel label={t("vorgang")} />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: H.accent,
              fontWeight: 500,
            }}
          >
            <Link2 width={13} height={13} strokeWidth={2} />#{f.vorgang.id} —{" "}
            {f.vorgang.title}
          </span>
        </>
      ) : null}
    </div>
  );
}

export function DokumenteDetailTabPanel(props: {
  readonly tab: DocDetailTab;
  readonly fixture: DocDetailFixture;
}): ReactNode {
  const t = useTranslations("marketing.moduleDokumenteDetailTabBodies");
  switch (props.tab) {
    case "details":
      return <DetailsTabBody fixture={props.fixture} />;
    case "ocr":
      return (
        <pre
          style={{
            margin: 0,
            padding: "14px 16px",
            fontFamily: H.label,
            fontSize: 11,
            lineHeight: 1.65,
            color: H.inkSoft,
            whiteSpace: "pre-wrap",
          }}
        >
          {props.fixture.ocrLines.join("\n")}
        </pre>
      );
    case "metadata":
      return (
        <div style={{ padding: "12px 14px" }}>
          {props.fixture.metadata.map((row) => (
            <div
              key={row.key}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                padding: "8px 0",
                borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
                fontSize: 12,
              }}
            >
              <span style={{ color: H.inkDim, flexShrink: 0 }}>{row.key}</span>
              <span
                style={{
                  color: H.inkSoft,
                  textAlign: "right",
                  wordBreak: "break-all",
                  maxWidth: "58%",
                }}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      );
    case "versions":
      return (
        <div style={{ padding: "12px 14px" }}>
          {props.fixture.versions.map((v) => (
            <div
              key={v.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "9px 0",
                borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
                fontSize: 12.5,
              }}
            >
              <span
                style={{
                  color: v.current === true ? H.accent : H.ink,
                  fontWeight: 500,
                }}
              >
                {v.label}
                {v.current === true ? ` ${t("aktuell")}` : ""}
              </span>
              <span style={{ color: H.inkDim, fontSize: 11.5 }}>{v.date}</span>
            </div>
          ))}
        </div>
      );
    case "actions":
      return (
        <div style={{ padding: "12px 14px" }}>
          {props.fixture.actionLog.map((entry) => (
            <div
              key={`${entry.label}-${entry.date}`}
              style={{
                display: "flex",
                gap: 10,
                padding: "8px 0",
                borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
              }}
            >
              <span
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: H.accentSoft,
                  color: H.accent,
                  fontSize: 9,
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {entry.initials}
              </span>
              <div>
                <div style={{ fontSize: 12, color: H.ink }}>{entry.label}</div>
                <div style={{ fontSize: 11, color: H.inkGhost, marginTop: 2 }}>
                  {entry.date}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
  }
}
